import PropTypes from 'prop-types';
import React from 'react';
import _isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';
import { faTimes } from '@fortawesome/free-solid-svg-icons/faTimes';

import Col from 'ravenjs/lib/Col';
import Divider from 'ravenjs/lib/Divider';
import FaIcon from 'ravenjs/lib/FaIcon';
import List from 'ravenjs/lib/List';
import ListItem from 'ravenjs/lib/ListItem';
import Row from 'ravenjs/lib/Row';
import Typography from 'ravenjs/lib/Typography';
import { getHandler, callFunc } from 'ravenjs/utils/actions';

import SelectedListFilters from './SelectedListFilters';

const ListFiltersStyled = styled.div``;

/**
 * A data filters component to dynamically render set of filters with given action handlers.
 *
 * @method      ListFilters
 * @param       {Object}    props Component props
 * @constructor
 */
function ListFilters(props) {
    const { clearable, clearFiltersLabel, filters, handlers, title, titleProps } = props;
    // Extract handlers.
    const onClearFilters = getHandler(handlers, 'onClearFilters');
    const onSelectedFilterRemove = getHandler(handlers, 'onSelectedFilterRemove');
    // If filters are empty, don't render the component.
    if (_isEmpty(filters)) {
        return null;
    }
    // Check if any filters are selected.
    function checkAreFiltersSelected() {
        return filters.some(({ selected = [] }) => Array.isArray(selected) && selected.length > 0);
    }
    // Render the `Clear Filters` sub-component.
    function renderClearFilters() {
        // If ListFilters is not clearable, don't render.
        if (!clearable) {
            return null;
        }
        // Render the 'Clear Filters' anchor.
        return (
            <Typography gutterBottom="8px" onClick={onClearFilters} type="link">
                <FaIcon height="15px" icon={faTimes} width="15px" margin="0" /> {clearFiltersLabel}
            </Typography>
        );
    }
    // Render the `ListFilters` title.
    function renderTitle() {
        // Are any filters selected?
        const areFiltersSelected = checkAreFiltersSelected();
        // If the filters aren't clearable, we want to just display the title.
        return (
            <Row alignItems="baseline" gutter={false} justify="space-between">
                {(!clearable || !areFiltersSelected) && (
                    <Typography gutterBottom="8px" type="subheading" {...titleProps}>
                        {title}
                    </Typography>
                )}
                {areFiltersSelected && renderClearFilters()}
                <Divider margin="" />
            </Row>
        );
    }
    // Render the `ListFilters` component.
    return (
        <ListFiltersStyled>
            {renderTitle()}
            <List backgroundColor="transparent" flush>
                {filters.map(filter => {
                    const {
                        acl = true,
                        id,
                        label = '',
                        onAddLabel = '+ Add',
                        onAddClickRef = '',
                        selected = [],
                    } = filter;
                    // If we don't want to render the filter.
                    if (!acl) {
                        return null;
                    }
                    // Extract the `onClick` handler for the label.
                    const onLabelClickHandler = getHandler(handlers, onAddClickRef);
                    // Build the `onClick` for the filter's `+ Add` button.
                    const onLabelClick = () => callFunc(onLabelClickHandler, filter);
                    // Render the filter.
                    return (
                        <ListItem key={id} dense divider>
                            <Row gutter={false} width="100%">
                                <Col gutter={false} size={{ xs: 6, md: 8 }}>
                                    <Typography
                                        type="paragraph"
                                        gutterBottom="0 !important"
                                        gutterTop="0"
                                        gutterRight="10px"
                                    >
                                        {label}
                                    </Typography>
                                </Col>
                                <Col gutter={false} size={{ xs: 6, md: 4 }}>
                                    <Typography
                                        fullWidth
                                        align="right"
                                        gutterBottom="0"
                                        gutterTop="0"
                                        type="link"
                                        onClick={onLabelClick}
                                    >
                                        {onAddLabel}
                                    </Typography>
                                </Col>
                                <Col gutter={false} size={12}>
                                    <SelectedListFilters
                                        parentId={id}
                                        onRemove={onSelectedFilterRemove}
                                        selected={selected}
                                    />
                                </Col>
                            </Row>
                        </ListItem>
                    );
                })}
            </List>
        </ListFiltersStyled>
    );
}

ListFilters.propTypes = {
    /**
     * Label to display for the 'Clear Filters' text.
     */
    clearFiltersLabel: PropTypes.string,
    /**
     * If true, the `ListFilters` will be clearable. Defaults to `true`.
     */
    clearable: PropTypes.bool,
    /**
     * List of filters to build.
     */
    filters: PropTypes.arrayOf(
        PropTypes.shape({
            /**
             * If true, the filter will not be rendered.
             */
            acl: PropTypes.bool,
            /**
             * A unique ID for the filter.
             * This will be used when adding filters,
             * or removing already selected filters.
             */
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            /**
             * A reference to the `onAddClick` callback function,
             * invoked when clicking on the `+ Add` button.
             */
            onAddClickRef: PropTypes.string,
            /**
             * Label to display for the '+ Add' text.
             */
            onAddLabel: PropTypes.string,
            /**
             * A list of selected filters for this filter.
             */
            selected: PropTypes.arrayOf(
                PropTypes.shape({
                    /**
                     * A unique ID for the selected filter.
                     */
                    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                    /**
                     * Label for the selected filter.
                     */
                    label: PropTypes.string,
                })
            ),
        })
    ),
    /**
     * A map of action handlers used by rendered components.
     * `onClearFilters` and `onSelectedFilterRemove` are the required handlers,
     * additional ones can be added.
     */
    handlers: PropTypes.shape({
        /**
         * Callback fired on `onClear` filters button.
         */
        onClearFilters: PropTypes.func,
        /**
         * Callback fired on `onClick` of a `x` button, for each individual selected filter.
         */
        onSelectedFilterRemove: PropTypes.func,
    }),
    /**
     * The title for the List Filters.
     * This is displayed when there are no selected filters present.
     */
    title: PropTypes.string,
    /**
     * Props passed down to the `Typography` component that renders the `title`.
     */
    titleProps: PropTypes.object,
};

ListFilters.defaultProps = {
    clearFiltersLabel: 'Clear Filters',
    clearable: true,
    filters: null,
    handlers: null,
    title: 'Data Filters',
    titleProps: null,
};

export default React.memo(ListFilters);
